// vue-i18n setup for the Vance Web-UI.
//
// The i18n instance is a singleton — every editor's `main.ts` installs it
// and awaits {@link i18nReady} before mounting its app. Locale resolution
// order at startup:
//   1. {@code getActiveLanguage()} — reads `webui.language` straight
//      from the data cookie (server-refreshed on every profile save).
//   2. {@code navigator.language} primary tag (`'de-DE'` → `'de'`).
//   3. `'en'`.
//
// English (en.ts) is the source, bundled eagerly and always the fallback:
// missing keys in any other locale fall through to it silently. Every other
// locale is generated by scripts/translate-client.py into locales/<code>.json
// and loaded only when it becomes active — the same for addon bundles, which
// hand their locale loaders over through `@vance/shared/i18n`.

import { createI18n, type I18n } from 'vue-i18n';
import type { App } from 'vue';
import {
  localeLoaders,
  onI18nMessages,
  type LazyMessageBundle,
  type LocaleMessageTree,
} from '@vance/shared/i18n';
import { getActiveLanguage, onMeChanged } from '@/platform';
import { trackAddonLocaleLoad } from './addonLocales';
import en from './en';
import { isUiLocale, pluralIndex, THREE_FORM_LOCALES, type Locale } from './locales';

export { UI_LOCALES, isUiLocale, type Locale } from './locales';

function resolveStartupLocale(): Locale {
  const fromActive = getActiveLanguage();
  if (fromActive && isUiLocale(fromActive)) return fromActive;
  if (typeof navigator !== 'undefined' && navigator.language) {
    const primary = navigator.language.toLowerCase().split('-')[0];
    if (isUiLocale(primary)) return primary;
  }
  return 'en';
}

const i18n: I18n<
  { en: typeof en },
  Record<string, never>,
  Record<string, never>,
  string,
  false
> = createI18n({
  // Vue 3 composition-API mode — `legacy: false` enables `useI18n()`
  // in `<script setup>` and the global `$t` function.
  legacy: false,
  globalInjection: true,
  // Starts in English; {@link i18nReady} switches once the startup locale's
  // messages are in.
  locale: 'en',
  fallbackLocale: 'en',
  messages: { en },
  // English's one | other rule is vue-i18n's default; these have three forms.
  pluralRules: Object.fromEntries(
    THREE_FORM_LOCALES.map((l) => [l, (choice: number, len: number) => pluralIndex(l, choice, len)]),
  ),
  // Suppress the warning per missing key — fallback is intentional.
  silentTranslationWarn: true,
  silentFallbackWarn: true,
  missingWarn: false,
  fallbackWarn: false,
});

/** The host's own generated locales. */
const hostLoaders = localeLoaders(
  import.meta.glob<LocaleMessageTree>('./locales/*.json', { import: 'default' }),
);

/** Lazy locales of every registered addon bundle, by bundle id. */
const addonLoaders = new Map<string, LazyMessageBundle>();

/** Bundle id → locales already merged, so a loader runs once per locale. */
const merged = new Map<string, Set<string>>();

/** Locales that are, or have been, active — addons registering later load these too. */
const wanted = new Set<string>();

const HOST = '@host';

function merge(id: string, locale: string, messages: LocaleMessageTree): void {
  try {
    i18n.global.mergeLocaleMessage(locale, messages);
  } catch (e) {
    // A malformed bundle must not take the page down — the keys then show in
    // English, which is a visible but survivable defect.
    console.warn(`[i18n] bundle '${id}' (${locale}) could not be merged`, e);
  }
}

async function loadInto(
  id: string,
  locale: string,
  load: (() => Promise<LocaleMessageTree>) | undefined,
): Promise<void> {
  if (!load) return;
  const done = merged.get(id) ?? new Set<string>();
  merged.set(id, done);
  if (done.has(locale)) return;
  done.add(locale);
  try {
    merge(id, locale, await load());
  } catch (e) {
    done.delete(locale);
    console.warn(`[i18n] locale '${locale}' of '${id}' could not be loaded`, e);
  }
}

/** Load the host's and every addon's messages for a locale. English is always there. */
async function ensureLocale(locale: Locale): Promise<void> {
  if (locale === 'en') return;
  wanted.add(locale);
  const jobs = [loadInto(HOST, locale, hostLoaders[locale])];
  for (const [id, lazy] of addonLoaders) jobs.push(loadInto(id, locale, lazy[locale]));
  await Promise.all(jobs);
}

// Addon translations. A federation remote bundles its own copy of every
// workspace package and therefore cannot reach `i18n` here, so it drops its
// bundle into the globalThis registry in `@vance/shared/i18n` and this merges
// it in. Subscribing at module scope covers both directions of the race: the
// registry replays what a remote registered before this ran, and later
// registrations (a lazily loaded addon) arrive through the same sink.
//
// Merged, not assigned: `mergeLocaleMessage` deep-copies, so an addon may
// extend a host namespace — its `documents.detail.tab*` key lands next to the
// built-in ones instead of replacing the branch.
//
// Only English comes eagerly; the active locale's json is fetched right after
// registration. That load is tracked (`./addonLocales`), and every host loader
// of an addon expose awaits it before handing the expose out — so a lazily
// loaded addon's first view paints in the user's language, not in English.
//
// An addon built against an older `@vance/shared` hands over no lazy locales
// (only `en`/`de` eagerly) — it simply has nothing to load later.
onI18nMessages((id, bundle, lazy = {}) => {
  for (const [locale, messages] of Object.entries(bundle)) {
    if (isUiLocale(locale)) merge(id, locale, messages);
  }
  addonLoaders.set(id, lazy);
  // A remote that arrives after the user picked a language catches up at once.
  const jobs = [...wanted].map((locale) => loadInto(id, locale, lazy[locale]));
  if (jobs.length > 0) trackAddonLocaleLoad(Promise.all(jobs).then(() => undefined));
});

/**
 * Bumped per locale request (startup and {@link setUiLocale}). Requests may
 * overlap — a slow `de` load, then an immediate `en` — and only the latest one
 * may flip the locale, or the earlier one would win late.
 */
let localeGeneration = 0;

function applyLocale(locale: Locale): void {
  // The exposed `.global.locale` is a Ref<string> when legacy is false.
  (i18n.global.locale as unknown as { value: string }).value = locale;
}

/**
 * Resolves once the startup locale's messages are loaded and active. Every
 * entry's `main.ts` awaits it before `mount()`, so the first paint is already
 * in the user's language. A failed load resolves too — the UI is English then.
 */
export const i18nReady: Promise<void> = (async () => {
  const locale = resolveStartupLocale();
  const generation = ++localeGeneration;
  await ensureLocale(locale);
  if (generation === localeGeneration) applyLocale(locale);
})();

// The startup locale came from the last `/me` snapshot; when the fresh answer
// carries a different language (changed elsewhere, or no snapshot yet), follow
// it. Cleared means back to the browser's language.
onMeChanged((next, previous) => {
  if (next?.settings?.['webui.language'] !== previous?.settings?.['webui.language']) {
    void setUiLocale(resolveStartupLocale());
  }
});

/** Install on the given Vue app. Call before {@code app.mount()}. */
export function installI18n(app: App): void {
  app.use(i18n);
}

/**
 * Switch the live UI language. Used by the profile page after the
 * user picks a different language and by the topbar's transient switch —
 * loads the locale's messages, then flips the i18n locale ref so all bound
 * templates re-render right away.
 *
 * Unsupported codes silently no-op (rather than throwing) — the user
 * has already saved the value to the server, the UI just doesn't
 * have a translation file yet.
 */
export async function setUiLocale(value: string | null): Promise<void> {
  const next = value ?? 'en';
  if (!isUiLocale(next)) return;
  const generation = ++localeGeneration;
  await ensureLocale(next);
  if (generation !== localeGeneration) return;
  applyLocale(next);
}

export { i18n };
